import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import axios from "axios";

const API_WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://') || 'ws://localhost:8001';
const API_HTTP_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [connected, setConnected] = useState(false);
  const [currentBrand, setCurrentBrand] = useState("all");
  const [usePolling, setUsePolling] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const pollingIntervalRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const MAX_RECONNECT_ATTEMPTS = 3;

  // Fallback polling for notifications
  const pollNotifications = useCallback(async (brand) => {
    try {
      const res = await axios.get(`${API_HTTP_URL}/api/notifications/${brand}?limit=20&unread_only=true`);
      const newNotifications = res.data.notifications || [];
      
      // Check for new notifications
      if (newNotifications.length > 0) {
        const existingIds = new Set(notifications.map(n => n.id));
        const brandNewNotifications = newNotifications.filter(n => !existingIds.has(n.id));
        
        if (brandNewNotifications.length > 0) {
          setNotifications(prev => [...brandNewNotifications, ...prev].slice(0, 50));
          setUnreadCount(prev => prev + brandNewNotifications.length);
          
          // Show toast for new notifications
          brandNewNotifications.forEach(notification => {
            const toastType = notification.severity === "error" ? toast.error :
                            notification.severity === "warning" ? toast.warning :
                            notification.severity === "success" ? toast.success :
                            toast.info;
            
            toastType(notification.title, {
              description: notification.message,
              duration: 5000,
            });
          });
        }
      }
    } catch (error) {
      console.error("Error polling notifications:", error);
    }
  }, [notifications]);

  const connect = useCallback((brand = "all") => {
    // Close existing connection
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.close();
    }

    const wsUrl = `${API_WS_URL}/ws/notifications?brand=${brand}`;
    console.log("Connecting to WebSocket:", wsUrl);

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      setCurrentBrand(brand);

      ws.onopen = () => {
        console.log("WebSocket connected");
        setConnected(true);
        setUsePolling(false);
        reconnectAttempts.current = 0;
        
        // Stop polling if it was running
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }
        
        // Start ping interval to keep connection alive
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000); // Ping every 30 seconds
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === "notification") {
            // Add to notifications list
            setNotifications(prev => [data, ...prev].slice(0, 50));
            setUnreadCount(prev => prev + 1);
            
            // Show toast notification
            const toastType = data.severity === "error" ? toast.error :
                            data.severity === "warning" ? toast.warning :
                            data.severity === "success" ? toast.success :
                            toast.info;
            
            toastType(data.title, {
              description: data.message,
              duration: 5000,
            });
          } else if (data.type === "connection") {
            console.log("Connection confirmed:", data.message);
          } else if (data.type === "pong") {
            // Keepalive response received
          }
        } catch (e) {
          console.error("Error parsing WebSocket message:", e);
        }
      };

      ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        setConnected(false);
      };

      ws.onclose = (event) => {
        console.log("WebSocket closed:", event.code, event.reason);
        setConnected(false);
        
        // Clear ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        
        reconnectAttempts.current++;
        
        // If max reconnect attempts reached, switch to polling
        if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
          console.log("Max reconnect attempts reached, switching to polling mode");
          setUsePolling(true);
          
          // Start polling every 30 seconds
          if (!pollingIntervalRef.current) {
            pollingIntervalRef.current = setInterval(() => {
              pollNotifications(brand);
            }, 30000);
            // Immediate first poll
            pollNotifications(brand);
          }
          return;
        }
        
        // Attempt reconnect after 5 seconds
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          console.log(`Attempting to reconnect (attempt ${reconnectAttempts.current + 1}/${MAX_RECONNECT_ATTEMPTS})...`);
          connect(brand);
        }, 5000);
      };
    } catch (error) {
      console.error("Failed to create WebSocket:", error);
      setConnected(false);
      
      // Fall back to polling
      setUsePolling(true);
      if (!pollingIntervalRef.current) {
        pollingIntervalRef.current = setInterval(() => {
          pollNotifications(brand);
        }, 30000);
        pollNotifications(brand);
      }
    }
  }, [pollNotifications]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
    }
    setConnected(false);
  }, []);

  const subscribeToBrand = useCallback((brand) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "subscribe", brand }));
      setCurrentBrand(brand);
    } else {
      connect(brand);
    }
  }, [connect]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  const markAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  // Initial connection
  useEffect(() => {
    connect("all");
    
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  const value = {
    notifications,
    unreadCount,
    connected,
    currentBrand,
    usePolling,
    connect,
    disconnect,
    subscribeToBrand,
    clearNotifications,
    markAsRead,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}
